import React from 'react'
import MortgageWidget, { BBMWidgetParams } from 'components/vendor/MortgageWidget'
import EmptyState from 'experiences/search/widgets/EmptyState'
import { COLORS, CONTENTFUL_KEYS, MortgageStateWhitelist } from 'parameters/index'
import ExplorePageLayout from './layouts/ExplorePageLayout'
import { InjectedContentfulProps, withContentful } from 'components/vendor/Contentful/withContentful'
import styled from '@emotion/styled'
import { CampaignIDs } from 'types/index'
import { ExplorePageReduxProps } from 'types/page'
import { SessionStatuses, useLocation } from 'services/FionaAPI/Lambdas/useLocation'
import { MortgageDisclosure } from 'components/content/MortgageDisclosure'
import { useHasMounted } from 'lib/renderWrappers/ClientOnly'
import { Nullable } from 'lib'

interface ContentProps {
	campaign: CampaignIDs
	mode?: 'refinance' | 'normal'
}

type Props = ExplorePageReduxProps & ContentProps

const MortgagePageContent = ({ campaign, mode }: ContentProps): Nullable<JSX.Element> => {
	const [{ status, location }] = useLocation()
	const hasMounted = useHasMounted()
	const locationEligible = location?.stateCode && MortgageStateWhitelist.includes(location.stateCode)
	const filterOverrides: BBMWidgetParams = mode === 'refinance' ? { loan_type: 'REFI' } : {}
	if (!hasMounted) return null

	if (locationEligible) {
		return <MortgageWidget defaultFilters={filterOverrides} campaign={campaign} />
	} else {
		return (
			<EmptyWrapper>
				{status != SessionStatuses.Default ? (
					<EmptyState
						title="Unfortunately we aren’t able to offer services in your area."
						subTitle="Explore the other products you can apply for through Fiona"
						hideMortgage
					/>
				) : (
					<></>
				)}
			</EmptyWrapper>
		)
	}
}

const MortgagePage = ({
	articles,
	campaign,
	getValue,
	title,
	subtitle,
	mode = 'normal'
}: Props & InjectedContentfulProps): JSX.Element => {
	return (
		<>
			<ExplorePageLayout
				articles={articles}
				title={title || 'Discover mortgages that may best fit your budget'}
				description={subtitle || getValue(CONTENTFUL_KEYS.PRODUCTS.MORTGAGE.DESCRIPTION)}
				background="/images/explore-mortgage.svg"
			>
				<MinHeight>
					<MortgagePageContent campaign={campaign} mode={mode} />
				</MinHeight>
			</ExplorePageLayout>
			<MortgageDisclosure />
		</>
	)
}

export default withContentful(MortgagePage)

const EmptyWrapper = styled.div`
	.emptyWrapper {
		min-height: 600px;
		border: none;
		box-shadow: 0 0 20px 0 rgba(69, 69, 69, 0.16);
		background-color: ${COLORS.WHITE};
	}
`

const MinHeight = styled.div`
	min-height: 600px;
`
